import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { sendShareOtp, verifyShareOtp } from '../services/superPickerPublicApi';
import { setShareSessionToken } from '../services/shareContext';

/**
 * Blocking dialog the SPA mounts when `resolveShareLink` returns a row
 * whose `activatedAt` is `null`. The recipient must:
 *
 *   1. Tap "Send code via WhatsApp" — backend generates + HMAC-hashes a
 *      6-digit OTP, persists it (10-minute TTL), and pushes the
 *      `super_picker_lite_otp` template to their phone.
 *   2. Type the code into the 6-digit input.
 *   3. Tap "Verify". On 200 the backend has set `ActivatedAt`; we
 *      forward the new `expiresAt` to the parent so the trial timer
 *      can start immediately.
 *
 * Resend is gated by a visible 60-second countdown that mirrors the
 * backend rate-limit (`OTP_COOLDOWN`).
 */
interface OtpVerificationDialogProps {
  open: boolean;
  uuid: string;
  /** Last-4-masked mobile shown in the dialog copy. */
  mobileMasked: string | null;
  /**
   * Called with the activation response after a successful verify. The
   * parent SPA uses the included `expiresAt` to seed the countdown chip
   * without a second resolve round-trip.
   */
  onActivated: (result: { activatedAt: string; expiresAt: string | null }) => void;
}

const RESEND_COOLDOWN_SEC = 60;

export const OtpVerificationDialog: React.FC<OtpVerificationDialogProps> = ({
  open,
  uuid,
  mobileMasked,
  onActivated,
}) => {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  /** ms-since-epoch at which the recipient may resend; 0 means "can send now". */
  const [resendReadyAt, setResendReadyAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  /** Tracks whether we've already auto-fired the first send. */
  const autoSentRef = useRef(false);

  // Tick once a second to drive the resend countdown chip.
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Reset transient state every time the dialog opens (e.g. user closes
  // browser tab, re-opens to a still-unactivated share).
  useEffect(() => {
    if (!open) {
      autoSentRef.current = false;
      setCode('');
      setError(null);
      setInfo(null);
      setResendReadyAt(0);
    }
  }, [open]);

  const remainingResendSec = useMemo(() => {
    if (resendReadyAt <= 0) return 0;
    return Math.max(0, Math.ceil((resendReadyAt - now) / 1000));
  }, [resendReadyAt, now]);

  const handleSend = useCallback(async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    setInfo(null);
    try {
      const res = await sendShareOtp(uuid);
      setInfo(
        `Code sent via WhatsApp${
          res.mobileMasked ?? mobileMasked
            ? ` to ${res.mobileMasked ?? mobileMasked}`
            : ''
        }. It expires in 10 minutes.`,
      );
      setResendReadyAt(Date.now() + RESEND_COOLDOWN_SEC * 1000);
    } catch (err) {
      const e = err as Error & { code?: string; retryAfterSec?: number };
      if (e.code === 'OTP_COOLDOWN' && typeof e.retryAfterSec === 'number') {
        setResendReadyAt(Date.now() + e.retryAfterSec * 1000);
      }
      if (e.code === 'SHARE_ALREADY_ACTIVATED') {
        // Edge case: another tab activated. Best to ask the user to
        // refresh; the parent will pick up the new state on next mount.
        setError('This link has already been activated. Please refresh the page.');
      } else {
        setError(e.message || 'Failed to send code. Please try again.');
      }
    } finally {
      setSending(false);
    }
  }, [sending, uuid, mobileMasked]);

  // Auto-send the first code when the dialog mounts. Keeps the UX one
  // tap shorter than forcing the recipient to press "Send" manually.
  useEffect(() => {
    if (!open || autoSentRef.current) return;
    autoSentRef.current = true;
    void handleSend();
  }, [open, handleSend]);

  const handleVerify = useCallback(async () => {
    if (verifying) return;
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code from WhatsApp.');
      return;
    }
    setVerifying(true);
    setError(null);
    setInfo(null);
    try {
      const result = await verifyShareOtp(uuid, code);
      // Persist the per-device session JWT BEFORE `onActivated` runs so
      // any subsequent `publicFetch` call from the parent (e.g. the
      // reveals-hydration round-trip in `SuperPickerPublicApp`) can
      // already pick it up via `getShareSessionToken`. Without this,
      // the gate would 401 the very first request after activation.
      if (result.sessionToken) {
        setShareSessionToken(uuid, result.sessionToken);
      }
      onActivated(result);
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === 'NO_PENDING_OTP') {
        setError('No active code found. Please send a new code.');
        setResendReadyAt(0);
      } else if (e.code === 'INVALID_OTP') {
        setError('Incorrect code. Please double-check WhatsApp and try again.');
      } else if (e.code === 'INVALID_OTP_FORMAT') {
        setError('Code must be 6 digits.');
      } else {
        setError(e.message || 'Verification failed. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  }, [verifying, code, uuid, onActivated]);

  const canResend = !sending && remainingResendSec <= 0;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      // No close affordance — recipient must verify to proceed. We
      // intentionally omit the close button and disableEscapeKeyDown so
      // the dialog truly blocks access.
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366' }} />
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Verify your phone
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            For your protection, this link requires a one-time code sent
            via WhatsApp{mobileMasked ? ` to ${mobileMasked}` : ''}.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
          {info && !error && <Alert severity="success">{info}</Alert>}

          <TextField
            label="6-digit code"
            value={code}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(next);
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 6,
              autoComplete: 'one-time-code',
              style: {
                fontSize: '1.5rem',
                letterSpacing: '0.5rem',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              },
            }}
            fullWidth
            autoFocus
            disabled={verifying}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6 && !verifying) {
                void handleVerify();
              }
            }}
          />

          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={() => void handleVerify()}
            disabled={verifying || code.length !== 6}
            startIcon={
              verifying ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {verifying ? 'Verifying…' : 'Verify and continue'}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => void handleSend()}
              disabled={!canResend}
              startIcon={
                sending ? <CircularProgress size={14} color="inherit" /> : null
              }
            >
              {sending
                ? 'Sending…'
                : canResend
                ? 'Resend code'
                : `Resend in ${remainingResendSec}s`}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            Didn't receive it? Check that WhatsApp is installed on the
            number ending in {mobileMasked ?? '••••'}, and tap "Resend code"
            after the countdown.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
